const _ = require("lodash");
const dagre = require("@dagrejs/dagre");
const workflowsSchema = require("./workflowsSchema.json");
const { uqlDefinitions, uqlCondensedSchema } = require("./uqlDefinitions");
const backwardsCompatibleBlockTypeLookup = require("./backwardsCompatibleBlockTypeLookup");
const randomString = require("@rflocal/shared/utils/randomString.js");
const {
    BLOCK_UI_SPECIFICATION,
    BLOCK_SECTIONS,
    CUSTOM_PYTHON_BLOCK,
    detection_types,
    detection_types_array
} = require("./constants");

class Manifest {
    /**
     * @param {Object} manifest
     */
    constructor(manifest) {
        this.manifest = this.initializeManifest(manifest);
        this.manifestSchema = workflowsSchema;
        this.manifestsByBlockType = this.buildBlockSchemaLookup();
        this.alternativeBlockTypeLookup = this.buildAlternativeBlockTypeLookup();
        this.blockInfoByType = this.buildBlockInfoByType();
        this.blockSections = this.buildBlockSections();
        this.newestVersionOfBlockFamily = this.buildNewestVersionLookup();
        this.detection_types = detection_types;
        this.detection_types_array = detection_types_array;
        this.operationsLookup = this.buildOperationsLookup();
        this.operatorsLookup = this.buildOperatorsLookup();
        this.uqlLookup = this.buildUQLLookup();
    }

    initializeManifest(m) {
        if (!m) m = {};
        if (!m.blocks) m.blocks = [];
        else m.blocks.push(CUSTOM_PYTHON_BLOCK);
        if (!m.primitives_connections) m.primitives_connections = [];
        if (!m.kinds_connections) m.kinds_connections = {};
        if (!m.outputs) m.outputs = [];
        if (!m.inputs) m.inputs = [];
        if (!m.steps) m.steps = [];
        if (!m.version) m.version = 1;
        m.sections = BLOCK_SECTIONS;

        return m;
    }

    loadManifestSchema(schema) {
        if (!schema) return;
        this.manifestSchema = schema;
    }

    buildBlockSchemaLookup() {
        const lookup = this.manifest.blocks.reduce((acc, block) => {
            const blockType = block.manifest_type_identifier;
            acc[blockType] = block;
            return acc;
        }, {});

        // Note: UI-only blocks are added to this lookup in buildBlockInfoByType() method
        // where BLOCK_UI_SPECIFICATION entries with ui_only=true are processed

        return lookup;
    }

    buildAlternativeBlockTypeLookup() {
        const lookup = this.manifest.blocks.reduce((acc, block) => {
            acc[block.manifest_type_identifier] = block.manifest_type_identifier;
            const alternativeTitles = block.manifest_type_identifier_aliases || [];
            alternativeTitles.forEach((title) => {
                acc[title] = block.manifest_type_identifier;
            });
            return acc;
        }, {});

        // Add UI-only blocks from constants
        for (const [blockType, blockUIInfo] of Object.entries(BLOCK_UI_SPECIFICATION)) {
            if (blockUIInfo.ui_only) {
                const versionedType = blockType + "@v1";
                if (!lookup[versionedType]) {
                    lookup[versionedType] = versionedType;
                }
            }
        }

        return lookup;
    }

    buildBlockInfoByType() {
        let blockInfoByType = {};

        // First, process all blocks from the backend manifest
        for (const block of this.manifest.blocks) {
            const canonicalType = this.getCanonicalBlockType(block.manifest_type_identifier);
            const versionAgnosticType = this.getVersionAgnosticBlockType(canonicalType);

            if (versionAgnosticType === "roboflow_core/seg-preview") {
                block.human_friendly_block_name = "SAM 3";
            }

            // Merge backend ui_manifest with frontend BLOCK_UI_SPECIFICATION
            // Frontend takes priority for existing blocks, but backend ui_manifest is used for enterprise blocks
            let blockUIInfo = {
                ...(block.block_schema?.ui_manifest || {}),
                ...(BLOCK_UI_SPECIFICATION[versionAgnosticType] || {})
            };
            // For enterprise blocks, ensure backend ui_manifest section is preserved
            if (block.block_schema?.ui_manifest?.enterprise_only) {
                blockUIInfo.section = block.block_schema.ui_manifest.section;
                blockUIInfo.enterprise_only = block.block_schema.ui_manifest.enterprise_only;
                blockUIInfo.local_only = block.block_schema.ui_manifest.local_only;
            }
            let blockSchema = block.block_schema ?? {};
            blockInfoByType[block.manifest_type_identifier] = {
                ...block,
                priority: blockUIInfo.blockPriority ?? 9999,
                section: blockUIInfo.section || "custom",
                icon: blockUIInfo.icon,
                popular: blockUIInfo.popular,
                inDevelopment: blockUIInfo.inDevelopment,
                needsGPU: blockUIInfo.needsGPU,
                gpuHeavyDisclaimer: blockUIInfo.gpuHeavyDisclaimer,
                warnings: blockUIInfo.warnings || [],
                deprecated: blockSchema.deprecated,
                supervision: blockUIInfo.supervision,
                inference: blockUIInfo.inference,
                opencv: blockUIInfo.opencv,
                requires_rf_key: !!blockUIInfo.requires_rf_key,
                enterprise_only: !!blockUIInfo.enterprise_only,
                local_only: !!blockUIInfo.local_only
            };
        }

        // Then, add UI-only blocks from BLOCK_UI_SPECIFICATION that aren't in the manifest
        for (const [blockType, blockUIInfo] of Object.entries(BLOCK_UI_SPECIFICATION)) {
            // Skip if this is a UI-only block and it's already in the manifest (loaded from backend)
            if (!blockUIInfo.ui_only) continue;

            // Check if this block was loaded from backend (with or without version)
            const existsInManifest = Object.keys(blockInfoByType).some((manifestType) =>
                manifestType.startsWith(blockType)
            );

            if (!existsInManifest) {
                const versionedType = blockType + "@v1"; // Default to v1 for UI-only blocks
                blockInfoByType[versionedType] = this.generateBlockPlaceholder(
                    blockType,
                    blockUIInfo,
                    versionedType
                );
            }
        }

        return blockInfoByType;
    }

    generateBlockPlaceholder(blockType, blockUIInfo, versionedType) {
        return {
            manifest_type_identifier: versionedType,
            human_friendly_block_name: blockUIInfo.name || blockType,
            block_schema: {
                block_type: blockType,
                name: "name",
                properties: {
                    type: { const: versionedType, type: "string", title: "Block Type" },
                    name: {
                        type: "string",
                        title: "Step Name",
                        default: blockUIInfo.name || blockType.replace(/_/g, " ")
                    }
                },
                required: ["type", "name"],
                short_description: blockUIInfo.short_description,
                long_description: blockUIInfo.long_description,
                ui_only: true
            },
            priority: blockUIInfo.blockPriority ?? 9999,
            section: blockUIInfo.section || "custom",
            icon: blockUIInfo.icon,
            popular: blockUIInfo.popular,
            enterprise_only: !!blockUIInfo.enterprise_only,
            local_only: !!blockUIInfo.local_only,
            ui_only: true
        };
    }

    buildBlockSections() {
        let sections = this.manifest.sections.reduce((acc, section, index) => {
            acc[section.id] = { ...section, index: index, blocks: [] };
            return acc;
        }, {});

        for (const blockType in this.blockInfoByType) {
            const blockInfo = this.blockInfoByType[blockType];
            if (blockInfo.deprecated) continue;

            if (sections[blockInfo.section]) {
                sections[blockInfo.section].blocks.push(blockType);
            } else {
                sections["custom"].blocks.push(blockType);
            }
        }

        Object.keys(sections).forEach((section) => {
            sections[section].blocks = _.sortBy(
                sections[section].blocks,
                (blockType) => this.blockInfoByType[blockType].priority
            );
        });

        return sections;
    }

    buildNewestVersionLookup() {
        const newestVersionLookup = {};

        for (let block of Object.values(this.blockInfoByType)) {
            const blockFamily = block?.manifest_type_identifier?.split("@")[0];

            let blockVersion = block?.block_schema?.version;
            if (newestVersionLookup[blockFamily]) {
                const currentVersion = newestVersionLookup[blockFamily];
                const currentVersionInt =
                    currentVersion && currentVersion.startsWith("v")
                        ? parseInt(currentVersion.slice(1), 10) || -1
                        : -1;

                const candidateVersionInt =
                    blockVersion && blockVersion.startsWith("v")
                        ? parseInt(blockVersion.slice(1), 10) || -1
                        : -1;

                if (candidateVersionInt > currentVersionInt) {
                    newestVersionLookup[blockFamily] = blockVersion;
                }
            } else {
                newestVersionLookup[blockFamily] = blockVersion;
            }
        }

        return newestVersionLookup;
    }

    buildOperationsLookup() {
        const uqlOperations =
            this.manifest.universal_query_language_description?.operations_description ||
            uqlCondensedSchema.operations_description;

        if (!uqlOperations) return {};

        const operationsLookup = {};
        for (const operation of uqlOperations) {
            operationsLookup[operation.operation_type] = operation;
        }
        return operationsLookup;
    }

    buildOperatorsLookup() {
        const uqlOperators =
            this.manifest.universal_query_language_description?.operators_descriptions ||
            uqlCondensedSchema.operators_descriptions;

        if (!uqlOperators) return {};

        const operatorsLookup = {};
        for (const operator of uqlOperators) {
            operatorsLookup[operator.operator_type] = operator;
        }
        return operatorsLookup;
    }

    buildUQLLookup() {
        const uqlDefs = this.manifest.uql_defs || uqlDefinitions;
        if (!uqlDefs) return {};
        return uqlDefs;
    }

    getOperationsLookup() {
        return this.operationsLookup;
    }

    getUQLDefs() {
        return this.uqlLookup;
    }

    getCanonicalBlockType(blockType) {
        const dealiased = this.alternativeBlockTypeLookup[blockType] || blockType;
        if (backwardsCompatibleBlockTypeLookup[dealiased]) {
            return backwardsCompatibleBlockTypeLookup[dealiased];
        }
        return dealiased;
    }

    getVersionAgnosticBlockType(blockType) {
        return Manifest.getVersionAgnosticBlockType(blockType);
    }

    getBlockInfo(blockType) {
        const manifest_type_identifier = this.alternativeBlockTypeLookup[blockType];
        return this.blockInfoByType[manifest_type_identifier];
    }

    getBlockIsEnterpriseOnly(blockType) {
        return this.getBlockInfo(blockType)?.enterprise_only || false;
    }

    getBlockIsLocalOnly(blockType) {
        return this.getBlockInfo(blockType)?.local_only || false;
    }

    getBlockUISpecification(blockType) {
        const canonicalBlockType = this.getCanonicalBlockType(blockType);
        const versionAgnosticType = this.getVersionAgnosticBlockType(canonicalBlockType);
        const blockInfo = this.getBlockInfo(canonicalBlockType);
        let uiSpec =
            BLOCK_UI_SPECIFICATION[versionAgnosticType] ||
            blockInfo?.block_schema?.ui_manifest ||
            {};
        uiSpec = {
            ...uiSpec,
            section: this.blockSections[uiSpec.section] || this.blockSections.custom
        };
        return uiSpec;
    }

    isDetectionsPredictionKind(kind) {
        return this.detection_types.includes(kind);
    }

    isClassificationPredictionKind(kind) {
        return kind === "Batch[classification_prediction]" || kind === "classification_prediction";
    }

    isRoboflowModel(blockType) {
        const versionAgnosticType = this.getVersionAgnosticBlockType(blockType);
        let roboflowModels = [
            "roboflow_core/roboflow_classification_model",
            "roboflow_core/roboflow_multi_label_classification_model",
            "roboflow_core/roboflow_object_detection_model",
            "roboflow_core/roboflow_keypoint_detection_model",
            "roboflow_core/roboflow_instance_segmentation_model",
            "roboflow_core/florence_2",
            "roboflow_core/qwen25vl",
            "roboflow_core/qwen3vl",
            "roboflow_core/smolvlm2"
        ];
        return roboflowModels.includes(versionAgnosticType);
    }

    isImageKind(kind) {
        return kind === "image" || kind === "Batch[image]";
    }

    isDetectionCombiner(blockType) {
        // a detection transformer has an input that's an array of detections
        // and an output that's a single detection
        const blockManifest = this.getBlockInfo(blockType)?.block_schema;
        if (!blockManifest) return false;

        var hasDetectionInput = _.find(blockManifest.properties, (p, prop) => {
            const inputTypes = this.getStepOutputRefKindsForInputProperty(blockType, prop);

            return _.intersection(inputTypes, this.detection_types_array).length > 0;
        });

        const outputTypes = this.getOutputPropertyKinds(blockType);

        var hasDetectionOutput = _.find(outputTypes, (types) => {
            return _.intersection(types, this.detection_types).length > 0;
        });

        return !!(hasDetectionInput && hasDetectionOutput);
    }

    isPopularBlock(blockType) {
        return this.getBlockInfo(blockType)?.popular;
    }

    isCustomBlock(blockType) {
        let blockInfo = this.getBlockInfo(blockType);
        return blockInfo?.block_source === "dynamic_workflows_blocks";
    }

    getManifestSchema() {
        return this.manifestSchema;
    }

    getDataKinds() {
        return this.manifest.declared_kinds;
    }

    getBlockSections() {
        return Object.values(this.blockSections).filter((section) => section.blocks.length > 0);
    }

    getBlockTypes() {
        // Return all block types including UI-only blocks
        return Object.keys(this.blockInfoByType);
    }

    getBlockSchema(blockType) {
        return this.getBlockInfo(blockType)?.block_schema || {};
    }

    getBlockDescription(blockType, type = "short") {
        const description =
            type == "short"
                ? this.getBlockInfo(blockType)?.block_schema?.short_description
                : this.getBlockInfo(blockType)?.block_schema?.long_description;

        // validate proper description format and convert to string if needed
        if (Array.isArray(description)) {
            return description.join("\n");
        }

        return description || "No description available";
    }

    getBlockDisplayName(blockType) {
        const displayName = this.getBlockInfo(blockType)?.human_friendly_block_name;
        return displayName || blockType;
    }

    getBlockIndex(blockType) {
        const index = this.getBlockInfo(blockType)?.block_schema?.block_index;
        return index || 9999;
    }

    getBlockIcon(blockType) {
        const blockInfo = this.getBlockInfo(blockType);
        return { icon: blockInfo.icon || "far fa-sparkles" };
    }

    getBlockSectionName(blockType) {
        const blockInfo = this.getBlockInfo(blockType);
        return blockInfo?.section;
    }

    getBlockColorScheme(blockType) {
        const blockInfo = this.getBlockInfo(blockType);
        const section = this.blockSections[blockInfo.section];
        return section.colorScheme;
    }

    getBlockWarnings(blockType) {
        const blockInfo = this.getBlockInfo(blockType);
        return blockInfo?.warnings;
    }

    getBlockKeywords(blockType) {
        const blockInfo = this.getBlockInfo(blockType);
        const keywords = blockInfo?.block_schema?.search_keywords || [];
        return keywords.join(", ");
    }

    getBlockUsesSupervision(blockType) {
        const blockInfo = this.getBlockInfo(blockType);
        return blockInfo?.supervision;
    }

    getBlockUsesInference(blockType) {
        const blockInfo = this.getBlockInfo(blockType);
        return blockInfo?.inference;
    }

    getBlockUsesOpenCV(blockType) {
        const blockInfo = this.getBlockInfo(blockType);
        return blockInfo?.opencv;
    }

    getBlockRequiresRoboflowKey(blockType) {
        const blockInfo = this.getBlockInfo(blockType);
        return blockInfo?.requires_rf_key;
    }

    getBlockSourceCodeURL(blockType) {
        const blockInfo = this.getBlockInfo(blockType);
        const fqn = blockInfo?.fully_qualified_block_class_name;
        let sourceCodeUrl = null;
        if (fqn?.indexOf("inference.core.workflows.core_steps") === 0) {
            const parts = fqn.split(".");
            // fqn: inference.core.workflows.core_steps.visualizations.background_color.v1.BackgroundColorVisualizationBlockV1"
            // maps to: https://github.com/roboflow/inference/blob/main/inference/core/workflows/core_steps/visualizations/background_color/v1.py

            // first, pop off everything after the version
            while (parts.length > 0 && parts[parts.length - 1].match(/v\d+/) === null) {
                parts.pop();
            }

            // then join
            let joined = parts.join("/");
            sourceCodeUrl = `https://github.com/roboflow/inference/blob/main/${joined}.py`;
        }
        return sourceCodeUrl;
    }

    getNewStepConfig(blockType, name) {
        const blockManifest = this.getBlockInfo(blockType).block_schema;

        const stepConfig = {};

        const props = blockManifest.required;

        for (const p of props) {
            if (p === "type") {
                stepConfig[p] =
                    blockManifest.properties.type.const || blockManifest.properties.type.enum[0];
            } else if (p === "name") {
                stepConfig[p] = name;
            } else {
                if (blockManifest.properties[p].default != undefined) {
                    stepConfig[p] = blockManifest.properties[p].default;
                } else {
                    stepConfig[p] = null;
                }
            }
        }

        // special case for the RoboflowDatasetUpload block
        // need to set a unique usage_quota_name for each block by default
        const canonicalBlockType = this.getCanonicalBlockType(blockType);

        if (canonicalBlockType.startsWith("roboflow_core/roboflow_dataset_upload")) {
            stepConfig["usage_quota_name"] = `upload_quota_${randomString(5)}`;
        }

        if (canonicalBlockType.startsWith("roboflow_core/model_monitoring_inference_aggregator")) {
            const workspace = typeof window !== "undefined" ? window.currentWorkspace : "default";
            stepConfig["unique_aggregator_key"] =
                `model_monitoring_aggregator_${workspace}_${randomString(5)}`;
        }

        // Configure Twilio SMS block
        if (canonicalBlockType.startsWith("roboflow_core/twilio_sms_notification")) {
            const workspace = typeof window !== "undefined" ? window.currentWorkspace : "default";
            stepConfig["cooldown_session_key"] =
                `twilio_sms_session_${workspace}_${randomString(5)}`;
        }

        // Configure Slack block
        if (canonicalBlockType.startsWith("roboflow_core/slack_notification")) {
            const workspace = typeof window !== "undefined" ? window.currentWorkspace : "default";
            stepConfig["cooldown_session_key"] =
                `slack_notification_session_${workspace}_${randomString(5)}`;
        }

        return stepConfig;
    }

    getLiteralTypeAnnotationForInputProperty(blockType, input) {
        const blockInfo = this.getBlockInfo(blockType);

        // Return empty for UI-only blocks
        if (blockInfo?.ui_only) {
            return { type_annotation: ["string"], enum: null, examples: [] };
        }

        const primitive_connection = this.manifest.primitives_connections.find((item) => {
            return (
                item.manifest_type_identifier === blockInfo.manifest_type_identifier &&
                item.property_name === input
            );
        });

        const property_types =
            blockInfo.block_schema?.properties[input]?.anyOf?.find((item) => {
                return item.enum;
            }) || blockInfo.block_schema?.properties[input];

        return {
            type_annotation: primitive_connection?.type_annotation,
            enum: property_types?.enum,
            default: blockInfo.block_schema?.properties[input]?.default,
            examples: blockInfo.block_schema?.properties[input]?.examples
        };
    }

    getStepOutputRefKindsForInputProperty(blockType, input) {
        const blockInfo = this.getBlockInfo(blockType);

        // Return empty for UI-only blocks
        if (blockInfo?.ui_only) {
            return [];
        }

        const kinds = [];

        for (const kind of Object.keys(this.manifest.kinds_connections)) {
            const kindConnections = this.manifest.kinds_connections[kind] || [];
            const filtered = kindConnections.filter((item) => {
                return (
                    item.manifest_type_identifier === blockInfo.manifest_type_identifier &&
                    item.property_name === input &&
                    (item.compatible_element == "step_output" ||
                        item.compatible_element == "any_data")
                );
            });

            const hasKindArray = filtered.some((item) => item.is_list_element);
            const hasNonArray = filtered.some((item) => !item.is_list_element);
            if (hasKindArray && filtered.length > 0) {
                kinds.push(`${kind}[]`);
            }
            if (hasNonArray && filtered.length > 0) {
                kinds.push(kind);
            }
        }

        return _.uniq(kinds);
    }

    getInputRefKindsForInputProperty(blockType, input) {
        const blockInfo = this.getBlockInfo(blockType);

        // Return empty for UI-only blocks
        if (blockInfo?.ui_only) {
            return [];
        }

        const kinds = [];

        for (const kind of Object.keys(this.manifest.kinds_connections)) {
            const kindConnections = this.manifest.kinds_connections[kind] || [];
            const filtered = kindConnections.filter((item) => {
                return (
                    item.manifest_type_identifier === blockInfo.manifest_type_identifier &&
                    item.property_name === input &&
                    (item.compatible_element == "workflow_parameter" ||
                        item.compatible_element == "workflow_image" ||
                        item.compatible_element == "workflow_video_metadata" ||
                        item.compatible_element == "any_data")
                );
            });

            const hasKindArray = filtered.some((item) => item.is_list_element);
            const hasNonArray = filtered.some((item) => !item.is_list_element);
            if (hasKindArray && filtered.length > 0) {
                kinds.push(`${kind}[]`);
            }
            if (hasNonArray && filtered.length > 0) {
                kinds.push(kind);
            }
        }

        return _.uniq(kinds);
    }

    getInputPropertiesOfKind(blockType, kind) {
        const blockInfo = this.getBlockInfo(blockType);

        // Return empty for UI-only blocks
        if (blockInfo?.ui_only) {
            return [];
        }

        const manifest_type_identifier = blockInfo.manifest_type_identifier;

        const kindConnections = this.manifest.kinds_connections[kind] || [];
        const filtered = kindConnections.filter((item) => {
            return item.manifest_type_identifier === manifest_type_identifier;
        });
        const result = filtered.map((item) => item.property_name);

        return _.uniq(result);
    }

    getOutputPropertyKinds(blockType) {
        const blockInfo = this.getBlockInfo(blockType);

        // Return empty for UI-only blocks
        if (blockInfo?.ui_only) {
            return {};
        }

        const outputsManifest = blockInfo?.outputs_manifest;
        var ret = {};
        _.each(outputsManifest, function (output) {
            ret[output.name] = _.map(output.kind, "name");
        });
        return ret;
    }

    getInputProps(blockType) {
        const blockManifest = this.getBlockInfo(blockType)?.block_schema;
        var props = {};
        _.each(blockManifest?.properties, (p, prop) => {
            props[prop] = {
                inputRefKinds: this.getInputRefKindsForInputProperty(blockType, prop),
                stepRefKinds: this.getStepOutputRefKindsForInputProperty(blockType, prop),
                literalTypes: this.getLiteralTypeAnnotationForInputProperty(blockType, prop)
                    .type_annotation,
                enum: this.getLiteralTypeAnnotationForInputProperty(blockType, prop).enum,
                examples: this.getLiteralTypeAnnotationForInputProperty(blockType, prop).examples,
                required: blockManifest.required.includes(prop),
                title: p.title,
                description: p.description,
                default: p.default,
                name: prop,
                type: p.type,
                blockType: this.alternativeBlockTypeLookup[blockType],
                private: p.private,
                relevantFor: p.relevant_for,
                alwaysVisible: p.always_visible,
                additionalSection: p.hidden,
                dropdownMetadata: p.values_metadata,
                recommendedParsers: p.recommended_parsers,
                hideDescription: p.hide_description,
                multiline: p.multiline
            };
        });
        return props;
    }

    getInputDetectionProps(blockType) {
        const blockManifest = this.getBlockInfo(blockType)?.block_schema;
        var detections = [];
        _.each(blockManifest.properties, (p, prop) => {
            const inputTypes = this.getStepOutputRefKindsForInputProperty(blockType, prop);
            if (_.intersection(inputTypes, this.detection_types).length > 0) {
                detections.push(prop);
            }
        });
        return detections;
    }

    isConditionalFlowRef(blockType) {
        return [
            "roboflow_core/continue_if",
            "roboflow_core/rate_limiter",
            "roboflow_core/delta_filter"
        ].includes(this.getVersionAgnosticBlockType(blockType));
    }

    isDetectionTransformer(blockType) {
        const blockManifest = this.getBlockInfo(blockType)?.block_schema;
        if (!blockManifest) return false;

        var hasDetectionInput = _.find(blockManifest.properties, (p, prop) => {
            const inputTypes = this.getStepOutputRefKindsForInputProperty(blockType, prop);
            return _.intersection(inputTypes, this.detection_types).length > 0;
        });

        const outputTypes = this.getOutputPropertyKinds(blockType);
        var hasDetectionOutput = _.find(outputTypes, (types) => {
            return _.intersection(types, this.detection_types).length > 0;
        });

        return !!(hasDetectionInput && hasDetectionOutput);
    }

    getWorkflowRefs(workflowSpec) {
        const refs = [];

        if (!workflowSpec) {
            return [];
        }

        // TODO:  EE CHECK SPECIAL INPUT HANDLING
        for (const input of workflowSpec.inputs) {
            if (input.type === "InferenceImage" || input.type == "WorkflowImage") {
                refs.push({ ref: `$inputs.${input.name}`, kind: ["Batch[image]", "image"] });
            } else if (input.type === "InferenceParameter" || input.type === "WorkflowParameter") {
                refs.push({
                    ref: `$inputs.${input.name}`,
                    kind: [
                        "dictionary",
                        "float_zero_to_one",
                        "*",
                        "string",
                        "integer",
                        "list_of_values",
                        "boolean",
                        "float",
                        "roboflow_model_id",
                        "roboflow_project"
                    ] // make this reflect the actual types / way to match against string literals?
                });
            } else if (input.type === "WorkflowVideoMetadata") {
                refs.push({ ref: `$inputs.${input.name}`, kind: ["video_metadata"] });
            }
        }

        for (const step of workflowSpec.steps) {
            const outputs = this.getOutputPropertyKinds(step.type);

            refs.push({ ref: `$steps.${step.name}.*`, kind: "*" });

            for (const [outputName, outputKinds] of Object.entries(outputs)) {
                if (outputName === "*") {
                    continue; // wildcard already added above
                }
                refs.push({ ref: `$steps.${step.name}.${outputName}`, kind: outputKinds });
            }

            for (const dynamicRef of this.getDynamicOutputRefs(step)) {
                refs.push(dynamicRef);
            }
        }

        return refs;
    }

    getDynamicOutputRefs(step) {
        const refs = [];

        // JSON Parser
        if (step.type === "roboflow_core/json_parser@v1") {
            const expectedFields = step.expected_fields || [];
            for (const field of expectedFields) {
                refs.push({ ref: `$steps.${step.name}.${field}`, kind: ["*"] });
            }
        }

        // JSON Parser
        if (step.type === "roboflow_core/environment_secrets_store@v1") {
            const secretVars = step.variables_storing_secrets || [];
            for (const secretVar of secretVars) {
                refs.push({
                    ref: `$steps.${step.name}.${secretVar.toLowerCase()}`,
                    kind: ["string"]
                });
            }
        }

        // LMM
        if (step.type === "roboflow_core/lmm@v1") {
            const jsonOutput = step.json_output || {};
            for (const field of Object.keys(jsonOutput)) {
                refs.push({ ref: `$steps.${step.name}.${field}`, kind: ["*"] });
            }
        }

        // Data Aggregator
        if (step.type === "roboflow_core/data_aggregator@v1") {
            const aggregationMode = step.aggregation_mode || {};
            for (const [dataKey, modes] of Object.entries(aggregationMode)) {
                for (const mode of modes) {
                    refs.push({ ref: `$steps.${step.name}.${dataKey}_${mode}`, kind: ["*"] });
                }
            }
        }

        return refs;
    }

    getWorkflowRefsAndSteps(workflowSpec) {
        let refs = this.getWorkflowRefs(workflowSpec).map((r) => r.ref);
        let blockRefs = refs.filter((r) => r.endsWith(".*")).map((r) => r.slice(0, -2));
        refs = refs.concat(blockRefs);
        return refs;
    }

    isNewestBlockOfFamily(blockIdentifier) {
        const parts = blockIdentifier.split("@");
        const blockFamily = parts.length > 0 ? parts[0] : "";
        const blockInfo = this.getBlockInfo(blockIdentifier);
        return blockInfo?.block_schema?.version == this.newestVersionOfBlockFamily[blockFamily];
    }

    isSegPreviewBlock(blockIdentifier) {
        return blockIdentifier.includes("seg-preview");
    }

    hasMissingRefs(workflowSpec, data, blockType = null) {
        if (!data) return false;
        data = JSON.parse(JSON.stringify(data));
        // Delete non-used fields from block definition
        if (blockType) {
            const canonicalType = this.getCanonicalBlockType(blockType);
            const inputProperties = this.getInputProps(canonicalType);
            Object.keys(data).forEach((key) => {
                if (!inputProperties[key]) {
                    delete data[key];
                }
            });
        }

        let dataStr = JSON.stringify(data);
        if (!dataStr) {
            return false;
        }
        const refOptions = this.getWorkflowRefsAndSteps(workflowSpec);
        refOptions.forEach((ref) => {
            const escapedRef = ref.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, "\\$1");
            const regex = new RegExp(`"${escapedRef}"`, "g");
            dataStr = dataStr.replace(regex, "VALID_REF");
        });
        const includesMissingRefs = dataStr.includes("$steps.") || dataStr.includes("$inputs.");
        return includesMissingRefs;
    }

    isMissingRequiredProp(data) {
        const stepType = data.type;
        const inputProperties = this.getInputProps(stepType);
        const requiredProps = Object.values(inputProperties)
            .filter((property) => property.required)
            .filter((property) => {
                return property.name !== "type";
            });
        const missingRequiredProps = requiredProps.filter((property) => {
            const value = data[property.name];
            return value === null || value === undefined;
        });
        return missingRequiredProps.length > 0;
    }

    getKindForWorkflowRef(workflowSpec, ref) {
        const refs = this.getWorkflowRefs(workflowSpec);
        const found = refs.find((r) => r.ref === ref);
        return found?.kind;
    }

    listWorkflowStepIds(workflowSpec) {
        return workflowSpec.steps.map((step) => `$steps.${step.name}`);
    }

    onlyContainsWildcard(kinds) {
        return kinds.length === 1 && kinds[0] === "*";
    }

    getCompatibleWorkflowStepOutputRefs(
        workflowSpec,
        kinds,
        currentStepId = null,
        allowWildcard = true
    ) {
        const allRefs = this.getWorkflowRefs(workflowSpec);
        let filteredRefs = allRefs
            .filter((ref) => {
                const isStepOutput = ref.ref.startsWith("$steps") && !ref.ref.endsWith(".*");
                const kindIntersects = _.intersection(ref.kind, kinds).length > 0;
                const containsWildcard = ref.kind.includes("*") || this.onlyContainsWildcard(kinds);
                if (!allowWildcard) {
                    return isStepOutput && kindIntersects;
                }
                return isStepOutput && (kindIntersects || containsWildcard);
            })
            .map((ref) => ref.ref);

        // If currentStepId is provided, filter out downstream steps to prevent cycles
        if (currentStepId && workflowSpec) {
            try {
                // Import graphUtils here to avoid circular dependencies
                const graphUtils = require("../react/components/Workflows/GraphComponents/graphUtils");
                const graph = graphUtils.parseWorkflowToGraph(workflowSpec);

                // Get all downstream nodes from the current step
                const dijkstraResult = dagre.graphlib.alg.dijkstra(graph, currentStepId);

                // Filter out refs that point to downstream steps
                filteredRefs = filteredRefs.filter((ref) => {
                    const stepMatch = ref.match(/(\$steps\.\w+)/);
                    if (stepMatch) {
                        const stepId = stepMatch[1];
                        // If there's a finite distance from currentStepId to this step, it's downstream
                        return dijkstraResult[stepId].distance === Infinity;
                    }
                    return true;
                });
            } catch (error) {
                // If cycle detection fails, fall back to original behavior
                console.warn("Failed to perform cycle detection:", error); // eslint-disable-line no-console
            }
        }

        return filteredRefs;
    }

    getCompatibleWorkflowInputRefs(workflowSpec, kinds) {
        const allRefs = this.getWorkflowRefs(workflowSpec);

        return allRefs
            .filter((ref) => {
                const isInputRef = ref.ref.startsWith("$inputs");

                // only want input refs here
                if (!isInputRef) {
                    return false;
                }

                // console.log("checking input ref", ref, "against kinds", kinds);
                if (kinds.includes("Batch[image]") || kinds.includes("image")) {
                    const noWildcardKindsOfRef = ref.kind.filter((k) => k !== "*");
                    return _.intersection(noWildcardKindsOfRef, kinds).length > 0;
                } else {
                    // workflow parameter
                    const kindIntersects = _.intersection(ref.kind, kinds).length > 0;
                    const containsWildcard =
                        ref.kind.includes("*") || this.onlyContainsWildcard(kinds);
                    return kindIntersects || containsWildcard;
                }
            })
            .map((ref) => ref.ref);
    }

    calculateInputTypes(blockType) {
        const inputProperties = this.getInputProps(blockType);

        let stepKinds = new Set();
        let inputKinds = new Set();
        let stepKindMap = {};
        let inputKindMap = {};
        let requiresModelOutput = false;

        for (const propKey of Object.keys(inputProperties)) {
            if (["type", "name"].includes(propKey)) {
                continue;
            }
            let property = inputProperties[propKey];
            let stepRefKinds = property.stepRefKinds || [];
            let inputRefKinds = property.inputRefKinds || [];
            stepRefKinds.forEach((kind) => stepKinds.add(kind));
            inputRefKinds.forEach((kind) => inputKinds.add(kind));
            let requiresModelOutput_ = stepRefKinds.some(
                (kind) => this.isModelOutputKind(kind) && property.required
            );
            if (requiresModelOutput_) requiresModelOutput = true;
            stepKindMap[propKey] = stepRefKinds;
            inputKindMap[propKey] = inputRefKinds;
        }
        return {
            stepKinds: Array.from(stepKinds),
            stepKindMap,
            inputKinds: Array.from(inputKinds),
            inputKindMap,
            requiresModelOutput: requiresModelOutput
        };
    }

    isModelOutputKind(kind) {
        return this.isDetectionsPredictionKind(kind) || this.isClassificationPredictionKind(kind);
    }

    calculateOutputTypes(blockType) {
        const outputProperties = this.getOutputPropertyKinds(blockType);
        const outputKinds = new Set();
        const outputKindMap = {};
        for (const [outputName, outputValues] of Object.entries(outputProperties)) {
            outputValues.forEach((kind) => outputKinds.add(kind));
            outputKindMap[outputName] = outputValues;
        }
        return { outputKinds: Array.from(outputKinds), outputKindMap };
    }

    calculateInputOutputTypes() {
        const inputOutputTypes = {};
        for (const blockType of this.getBlockTypes()) {
            const { stepKinds, stepKindMap, inputKinds, inputKindMap, requiresModelOutput } =
                this.calculateInputTypes(blockType);
            const { outputKinds, outputKindMap } = this.calculateOutputTypes(blockType);
            // console.log({ blockType, stepKinds, inputKinds, outputKinds });
            inputOutputTypes[blockType] = {
                stepKinds,
                inputKinds,
                outputKinds,
                requiresModelOutputRef: requiresModelOutput,
                stepKindMap,
                inputKindMap,
                outputKindMap
            };
        }
        // console.log({ inputOutputTypes });
        return inputOutputTypes;
    }

    kindsMatch(kinds1, kinds2) {
        return (
            kinds1.some((item) => kinds2.includes(item)) ||
            this.onlyContainsWildcard(kinds1) ||
            this.onlyContainsWildcard(kinds2)
        );
    }

    getLatestBlockTypeForFamily(blockFamily) {
        const version = this.newestVersionOfBlockFamily[blockFamily];
        if (version) {
            return `${blockFamily}@${version}`;
        }
        return blockFamily;
    }

    /**
     * Static version of getVersionAgnosticBlockType for use without a Manifest instance.
     * Strips version suffix from block type.
     * Example: "roboflow_core/anthropic_claude@v1" -> "roboflow_core/anthropic_claude"
     *
     * @param {string} blockType - The block type identifier (may include version)
     * @returns {string} - The version-agnostic block type
     */
    static getVersionAgnosticBlockType(blockType) {
        return blockType ? blockType.split("@")[0] : blockType;
    }

    /**
     * Instance method that normalizes a block type for policy/governance checking.
     * For custom Python blocks (dynamic block instances), returns "custom_python_block".
     * For regular blocks, strips version suffix.
     *
     * @param {string} blockType - The block type identifier (may include version)
     * @returns {string} - The normalized block type for policy checking
     */
    getBlockTypeForPolicyCheck(blockType) {
        if (!blockType) return blockType;

        // Strip version suffix
        const versionAgnostic = Manifest.getVersionAgnosticBlockType(blockType);

        // Check if this is a dynamic block instance (e.g., "Custom_Python_Block_Test", "Another_Custom_Block")
        // These are created from the custom_python_block template and should map to it for policy checking
        const blockInfo = this.getBlockInfo(blockType);
        if (blockInfo?.block_source === "dynamic_workflows_blocks") {
            return "custom_python_block";
        }

        return versionAgnostic;
    }
}

// Export static utility function for use without instantiating Manifest
const getVersionAgnosticBlockType = Manifest.getVersionAgnosticBlockType;

module.exports = { Manifest, getVersionAgnosticBlockType };
